
import { defineComponent, ref, reactive, toRefs, onMounted, onUnmounted, computed, nextTick, watch } from 'vue'
import * as echarts from 'echarts'
import Nodata from '@/components/page/nodata.vue'
import f10ApiUS from '@/api/f10ApiUS'
import { useRoute } from 'vue-router'
import { isLocalDev } from '@/api/config'
import { InfoCircleOutlined } from '@ant-design/icons-vue'
export default defineComponent({
  props: {
    itemdata: {
      type: Object,
      default: () => ({})
    },
    pageSourceData: {
      type: Object,
      default: () => ({})
    },
    theme: {
      type: String,
      default: () => ''
    },
    token: {
      type: String,
      default: () => ''
    }
  },
  components: { Nodata, InfoCircleOutlined },
  setup(props, context) {
    const route = useRoute()
    const state = reactive({
      financialData: [] as any[],
      spinning: true,
      showYearOverYear: false,
      activeIndicator: 'totalAssets',
      activeReportType: 0,
      reportCate: 1,
      showImg: true,
      simpleImage: '',
      indicators: [
        // 资产合计及明细
        { key: 'totalAssets', title: '资产合计', indent: 0 },
        { key: 'cash', title: '其中: 现金及现金等价物', indent: 1 },
        { key: 'shortTermInvest', title: '短期投资', indent: 1 },
        { key: 'accountReceivable', title: '应收账款', indent: 1 },
        { key: 'dtAssetsCurrentAssets', title: '流动资产递延所得税', indent: 1 },
        { key: 'prepaidExpense', title: '预付款项', indent: 1 },
        { key: 'currentAssetsSpecialSubject', title: '流动资产特殊项目', indent: 1 },
        { key: 'totalCash', title: '总现金', indent: 1 },
        { key: 'deposit', title: '定期存款', indent: 1 },
        { key: 'totalCurrentAssets', title: '流动资产合计', indent: 1 },
        { key: 'goodWill', title: '商誉', indent: 1 },
        { key: 'netIntangibleAssets', title: '净无形资产', indent: 1 },
        { key: 'accumAmortization', title: '累计摊销', indent: 1 },
        { key: 'deferredTaxAssets', title: '非流动资产递延所得税', indent: 1 },
        { key: 'ncaSi', title: '非流动资产特殊项目', indent: 1 },
        { key: 'totalNonCurrentAssets', title: '非流动资产合计', indent: 1 },

        // 负债合计及明细
        { key: 'totalLiability', title: '负债合计', indent: 0 },
        { key: 'shortTermLoan', title: '短期借款', indent: 1 },
        { key: 'accountsPayable', title: '应付账款及票据', indent: 1 },
        { key: 'accruedExpensesEtc', title: '应计费用及其他应付款', indent: 1 },
        { key: 'totalCurrentLiability', title: '流动负债合计', indent: 1 },
        { key: 'ltDebt', title: '长期债务', indent: 1 },
        { key: 'totalNonCurrentLia', title: '非流动负债合计', indent: 1 },

        // 权益合计及明细
        { key: 'totalEquity', title: '股东权益合计', indent: 0 },
        { key: 'ePreferStock', title: '优先股', indent: 1 },
        { key: 'commonStock', title: '普通股', indent: 1 },
        { key: 'equityAtsopcSi', title: '归属于母公司股东权益特殊科目', indent: 1 },
        { key: 'totalHoldersEquity', title: '归属于母公司股东权益合计', indent: 1 },
        { key: 'minorityInterest', title: '归属于少数股东权益', indent: 1 },
        { key: 'totalEquitySpecialSubject', title: '权益特殊科目', indent: 1 }
      ],
      chartInstance: null as echarts.ECharts | null
    })

    // 计算报告期数据
    // const reportPeriods = computed(() => {
    //   if (!state.financialData.length) return []

    //   // 按报告日期排序（从大到小，最新的在前）
    //   const sortedData = [...state.financialData].sort((a, b) =>
    //     new Date(b.reportDate).getTime() - new Date(a.reportDate).getTime()
    //   )

    //   // 根据选择的报告类型过滤
    //   if (state.activeReportType !== 0) {
    //     return sortedData.filter(item => item.reportType === state.activeReportType)
    //   }

    //   // 按报告期时只取前11条（最新的11条）
    //   return sortedData.slice(0, 11)
    // })
    // 计算报告类型数据
    const reportTypes = computed(() => {
      const baseTypes = [
        { label: '按报告期', value: 0 },
        { label: '年报', value: 4 },
        { label: '三季报', value: 3 },
        { label: '中报', value: 2 },
        { label: '一季报', value: 1 }
      ]

      // 当reportCate === 2（单季模式）时，修改标签
      if (state.reportCate === 2) {
        return baseTypes.map(item => {
          if (item.value === 4) {
            return { ...item, label: '四季度' }
          } else if (item.value === 2) {
            return { ...item, label: '二季度' }
          }
          return item
        })
      }

      return baseTypes
    })

    // 计算报告期数据
    const reportPeriods = computed(() => {
      if (!state.financialData.length) return []

      // 按报告日期排序（从大到小，最新的在前）
      const sortedData = state.financialData.slice().sort((a, b) =>
        new Date(b.reportDate).getTime() - new Date(a.reportDate).getTime()
      )

      // 根据选择的报告类型过滤
      let filteredData = sortedData;
      if (state.activeReportType !== 0) {
        filteredData = sortedData.filter(item => item.reportType === state.activeReportType)
      }

      // 无论哪种报告类型，都只取前11条（最新的11条）
      return filteredData.slice(0, 10)
    })
    // 切换报告类型处理
    const handleReportTypeChange = () => {
      nextTick(() => {
        updateChart()
        // 切换报告类型后同步行高
        setTimeout(() => {
          syncTableRowHeights()
        }, 100)
      })
    }

    // 切换同比显示
    const toggleYearOverYear = () => {
      state.showYearOverYear = !state.showYearOverYear
      updateChart() // 切换同比时更新图表
      loadData()
      // 切换同比显示后同步行高
      nextTick(() => {
        setTimeout(() => {
          syncTableRowHeights()
        }, 100)
      })
    }

    // 切换单季开关：未选中时reportCate为1，选中时reportCate为2
    const toggleSingleQuarter = () => {
      state.reportCate = state.reportCate === 1 ? 2 : 1
      loadData()
      // 切换报告类别后同步行高
      nextTick(() => {
        setTimeout(() => {
          syncTableRowHeights()
        }, 100)
      })
    }

    const handleReportCateChange = () => {
      loadData()
      // 切换报告类别后同步行高
      nextTick(() => {
        setTimeout(() => {
          syncTableRowHeights()
        }, 100)
      })
    }

    // 选择指标
    const selectIndicator = (item: any) => {
      state.activeIndicator = item.key
      updateChart()
    }

    // 获取缩进类名
    const getIndentClass = (indent: number) => {
      if (indent === 1) return 'indent1'
      if (indent === 2) return 'indent2'
      return ''
    }

    // 获取指标值
    const getIndicatorValue = (key: string, period: any) => {
      return period[key]?.value ?? null
    }

    // 获取同比值
    const getYoyValue = (key: string, period: any) => {
      const currentValue = period[key]?.value;
      if (currentValue === null || currentValue === undefined) return null;

      // 找到去年同期的报告期数据
      const currentDate = new Date(period.reportDate);
      const lastYearDate = new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), currentDate.getDate());

      // 在financialData中查找去年同期的数据
      const lastYearPeriod = state.financialData.find((item) => {
        const itemDate = new Date(item.reportDate);
        return item.reportType === period.reportType &&
          itemDate.getFullYear() === lastYearDate.getFullYear() &&
          itemDate.getMonth() === lastYearDate.getMonth() &&
          itemDate.getDate() === lastYearDate.getDate();
      });

      if (!lastYearPeriod) return null;

      const lastYearValue = lastYearPeriod[key]?.value;
      if (lastYearValue === null || lastYearValue === undefined || lastYearValue === 0) return null;

      return (currentValue - lastYearValue) / lastYearValue;
    }

    // 获取同比样式类
    const getYoyClass = (key: string, period: any) => {
      const yoy = getYoyValue(key, period)
      if (yoy === null || yoy === undefined) return ''
      return yoy >= 0 ? 'yoy-positive' : 'yoy-negative'
    }

    // 格式化数值 - 修改为无值时显示 "--"
    // 格式化数值 - 修改为无值时显示 "--"
    const formatValue = (value: number | null) => {
      if (value === null) return '--'

      // 处理负数的绝对值
      const absValue = Math.abs(value)
      const isNegative = value < 0

      let formattedValue = ''
      if (absValue >= 1000000000000) {
        formattedValue = (absValue / 1000000000000).toFixed(2) + '万亿'
      } else if (absValue >= 100000000) {
        formattedValue = (absValue / 100000000).toFixed(2) + '亿'
      } else if (absValue >= 10000) {
        formattedValue = (absValue / 10000).toFixed(2) + '万'
      } else {
        formattedValue = absValue.toFixed(2)
      }

      // 如果是负数，添加负号
      return isNegative ? '-' + formattedValue : formattedValue
    }

    // 格式化同比 - 修改为无值时显示 "--"，去除+/-符号
    const formatYoy = (yoy: number | null) => {
      if (yoy === null || yoy === undefined) return '--'
      const value = yoy * 100
      return value.toFixed(2) + '%'
    }


    // 格式化日期
    const formatDate = (timestamp: number) => {
      const date = new Date(timestamp)
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
    }

    // 根据statementYear和reportType生成美股报告期日期
    const getReportDate = (statementYear: number, reportType: number) => {
      let month = 3
      let day = 31

      switch (reportType) {
        case 1: // 一季报 3-31
          month = 3
          day = 31
          break
        case 2: // 二季报 6-30
          month = 6
          day = 30
          break
        case 3: // 三季报 9-30
          month = 9
          day = 30
          break
        case 4: // 四季报 12-31
          month = 12
          day = 31
          break
      }
      return `${statementYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
    }

    // 初始化图表
    const initChart = () => {
      const chartDom = document.getElementById('chartContainerzcfzb')
      if (chartDom) {
        state.chartInstance = echarts.init(chartDom)
        updateChart()
      }
    }
    watch(
      () => route.query.theme,
      (theme: any) => {
        if (theme === 'dark') {
          state.simpleImage = require('@/assets/nodata_dark_v.png')
        } else {
          state.simpleImage = require('@/assets/nodata_light_v.png')
        }
      },
      { immediate: true }
    )
    // 更新图表
    const updateChart = () => {
      if (!state.chartInstance) return

      // 对报告期按日期升序排序（从小到大）
      const sortedPeriods = reportPeriods.value.slice().sort((a, b) =>
        new Date(a.reportDate).getTime() - new Date(b.reportDate).getTime()
      )

      const indicatorData = sortedPeriods.map(period => ({
        date: formatDate(period.reportDate),
        value: getIndicatorValue(state.activeIndicator, period),
        dataTime: getReportDate(period.statementYear, period.reportType),
        yoy: getYoyValue(state.activeIndicator, period)
      }))

      console.log('indicatorData:', indicatorData) // 调试用
      console.log('showYearOverYear:', state.showYearOverYear) // 调试用

      const activeIndicator = state.indicators.find(i => i.key === state.activeIndicator)
      const hasValidData = indicatorData.some(item => item.value !== null && item.value !== undefined && item.value !== '')
      state.showImg = hasValidData
      // 动态确定Y轴单位 - 统一处理正负数情况
      const values = indicatorData.map(item => item.value || 0)
      const maxValue = Math.max.apply(Math, values)
      const minValue = Math.min.apply(Math, values)
      // 取最大值和最小值的绝对值中的较大者来确定单位
      const maxAbsValue = Math.max(Math.abs(maxValue), Math.abs(minValue))
      let unit = '元'
      if (maxAbsValue >= 1000000000000) {
        unit = '万亿'
      } else if (maxAbsValue >= 100000000) {
        unit = '亿'
      } else if (maxAbsValue >= 10000) {
        unit = '万'
      }

      // 根据图片样式优化图表
      const option = {
        title: {
          // text: activeIndicator?.title || '财务指标',
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 'bold',
            color: props.theme === 'dark' ? '#d7d7df' : '#333'
          }
        },
        tooltip: {
          show: true,
          formatter: function (params: any) {
            console.log('Tooltip params:', params) // 调试用
            if (!params) return '无数据'

            const date = params.name || '未知日期'
            const value = params.value
            const seriesName = params.seriesName

            if (value === null || value === undefined) return `${date}<br/>数值: --`

            // 获取当前数据点的所有数据
            const dataIndex = params.dataIndex
            const currentData = indicatorData[dataIndex]

            let tooltipContent = `${date}<br/>`

            // 显示数值数据
            if (currentData && currentData.value !== null && currentData.value !== undefined) {
              const formattedValue = formatValue(currentData.value)
              tooltipContent += `<span style="display: inline-block; width: 8px; height: 8px; background-color: #1890ff; margin-right: 6px;margin-top: -6px;"></span>${activeIndicator?.title}: ${formattedValue}<br/>`
            }

            // 显示同比数据（根据开关控制）
            if (state.showYearOverYear && currentData && currentData.yoy !== null && currentData.yoy !== undefined) {
              const formattedYoy = formatYoy(currentData.yoy)
              const yoyColor = currentData.yoy >= 0 ? '#FF0000' : '#26A230' // 红涨绿跌
              tooltipContent += `<span style="display: inline-block; width: 8px; height: 3px; 
              background-color: #FFB63C; margin-right: 6px;margin-bottom: 3px;
              "></span>同比: <span style="color: ${yoyColor};">${formattedYoy}</span><br/>`
            }

            return tooltipContent
          }

        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: indicatorData.map(item => item.dataTime),
          axisLine: {
            lineStyle: {
              color: props.theme === 'dark' ? '#252a34' : '#ccc'
            }
          },
          axisLabel: {
            interval: 0,
            rotate: 0,
            color: props.theme === 'dark' ? '#d7d7df' : '#333'
          }
        },
        yAxis: [
          {
            type: 'value',
            name: activeIndicator?.title || '财务指标',
            nameLocation: 'end',
            nameGap: 20,
            nameTextStyle: {
              color: props.theme === 'dark' ? '#d7d7df' : '#666'
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: props.theme === 'dark' ? '#252a34' : '#ccc'
              }
            },
            splitLine: {
              lineStyle: {
                color: props.theme === 'dark' ? '#252a34' : '#f0f0f0',
                type: 'dashed'
              }
            },
            axisLabel: {
              color: props.theme === 'dark' ? '#d7d7df' : '#333',
              formatter: (value: number) => {
                // 处理负数的绝对值
                const absValue = Math.abs(value)
                const isNegative = value < 0

                let formattedValue = ''
                if (unit === '万亿') {
                  formattedValue = (absValue / 1000000000000).toFixed(2) + '万亿'
                } else if (unit === '亿') {
                  formattedValue = (absValue / 100000000).toFixed(2) + '亿'
                } else if (unit === '万') {
                  formattedValue = (absValue / 10000).toFixed(2) + '万'
                } else {
                  formattedValue = absValue.toFixed(2)
                }

                // 如果是负数，添加负号
                return isNegative ? '-' + formattedValue : formattedValue
              }
            }
          },
          {
            type: 'value',
            name: '同比',
            nameLocation: 'end',
            nameGap: 20,
            nameTextStyle: {
              color: props.theme === 'dark' ? '#d7d7df' : '#666'
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: props.theme === 'dark' ? '#252a34' : '#ccc'
              }
            },
            splitLine: {
              lineStyle: {
                color: props.theme === 'dark' ? '#252a34' : '#f0f0f0',
                type: 'dashed'
              }
            },
            axisLabel: {
              color: props.theme === 'dark' ? '#d7d7df' : '#333',
              formatter: (value: number) => {
                return formatYoy(value)
              }
            },
            // 使用show控制显示/隐藏
            show: state.showYearOverYear
          }
        ],
        series: [
          {
            name: '数值',
            type: 'bar',
            yAxisIndex: 0,
            barWidth: '30%',
            data: indicatorData.map(item => item.value),
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#1890ff' },
                { offset: 1, color: '#096dd9' }
              ])
            },
            emphasis: {
              itemStyle: {
                color: '#40a9ff'
              }
            },
            label: {
              show: true,
              position: 'top',
              formatter: (params: any) => {
                return formatValue(params.value)
              },
              color: props.theme === 'dark' ? '#d7d7df' : '#333',
              fontSize: 12,
              fontWeight: 'bold'
            }
          },
          {
            name: '同比',
            type: 'line',
            yAxisIndex: 1,
            data: state.showYearOverYear ? indicatorData.map(item => item.yoy) : [],
            lineStyle: {
              color: '#FFB63C',
              width: 2
            } as any,
            itemStyle: {
              color: '#FFB63C',
              borderWidth: 2
            } as any,
            symbol: 'circle',
            symbolSize: 6,
            label: {
              show: false,
              position: 'top',
              formatter: (params: any) => {
                return formatYoy(params.value)
              },
              color: '#FFB63C'
            },
            // 使用show控制显示/隐藏
            show: state.showYearOverYear
          } as any
        ]
      }

      state.chartInstance.setOption(option)
    }

    // 加载数据
    const loadData = () => {
      try {
        const market = isLocalDev ? route.query.market : (route.query.market as string)
        const symbol = isLocalDev ? route.query.code : (route.query.code as string)

        const data = {
          data: {
            market,
            symbol,
            tableType: '2',
            reportCate: state.reportCate
          },
          headers: {
            token: props.token || '3d5eff0d3c7ac7f3af30c337dcdf6944'
          }
        }

        f10ApiUS.getGwgk(data).then((res: any) => {
          if (res && res.data) {
            state.financialData = res.data.balanceList || []
            state.spinning = false
            nextTick(() => {
              initChart()
              // 延迟同步行高，确保表格完全渲染
              setTimeout(() => {
                syncTableRowHeights()
              }, 100)
            })
          }
          state.spinning = false
        }).catch(() => {
          state.spinning = false
        })
      } catch (error) {
        console.error('加载财务数据失败:', error)
        state.spinning = false
      }
    }

    // 同步表格行高
    const syncTableRowHeights = () => {
      nextTick(() => {
        const fixedTable = document.querySelector('.fixed-table tbody')
        const scrollTable = document.querySelector('.scroll-table tbody')

        if (fixedTable && scrollTable) {
          const fixedRows = fixedTable.querySelectorAll('tr')
          const scrollRows = scrollTable.querySelectorAll('tr')

          // 确保行数一致
          const minRows = Math.min(fixedRows.length, scrollRows.length)

          for (let i = 0; i < minRows; i++) {
            const fixedRow = fixedRows[i] as HTMLElement
            const scrollRow = scrollRows[i] as HTMLElement

            // 先重置高度，让内容自然渲染
            fixedRow.style.height = 'auto'
            scrollRow.style.height = 'auto'

            // 等待一帧后获取实际高度
            requestAnimationFrame(() => {
              const fixedHeight = fixedRow.offsetHeight
              const scrollHeight = scrollRow.offsetHeight

              // 使用较大的高度作为统一高度
              const maxHeight = Math.max(fixedHeight, scrollHeight, 30) // 最小30px

              // 设置统一高度
              fixedRow.style.height = maxHeight + 'px'
              scrollRow.style.height = maxHeight + 'px'

              // 确保单元格内容垂直居中
              const fixedCells = fixedRow.querySelectorAll('td')
              const scrollCells = scrollRow.querySelectorAll('td')

              fixedCells.forEach(cell => {
                const cellElement = cell as HTMLElement
                cellElement.style.verticalAlign = 'middle'
                cellElement.style.height = 'auto'
              })

              scrollCells.forEach(cell => {
                const cellElement = cell as HTMLElement
                cellElement.style.verticalAlign = 'middle'
                cellElement.style.height = 'auto'
              })
            })
          }
        }
      })
    }

    // 监听窗口大小变化
    const handleResize = () => {
      // 窗口大小变化时重新同步行高
      setTimeout(() => {
        syncTableRowHeights()
      }, 100)
    }

    onMounted(() => {
      loadData()
      // 添加窗口大小变化监听
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      // 组件卸载时清理
      window.removeEventListener('resize', handleResize)
    })
    // 根据标题宽度计算图标位置
    const getTooltipIconPosition = () => {
      const activeIndicator = state.indicators.find(i => i.key === state.activeIndicator)
      const title = activeIndicator?.title || '财务指标'

      // 基础位置 + 标题长度 * 字符宽度 + 间距
      const basePosition = 60 // Y轴标题起始位置
      const charWidth = 8 // 每个字符宽度
      const spacing = 28 // 图标与标题的间距
      console.log(title.length)
      return basePosition + (title.length * charWidth) + spacing
    }
    return {
      ...toRefs(state),
      reportTypes,
      reportPeriods,
      toggleYearOverYear,
      toggleSingleQuarter,
      handleReportCateChange,
      selectIndicator,
      getIndicatorValue,
      getYoyValue,
      getYoyClass,
      formatValue,
      formatYoy,
      formatDate,
      getReportDate,
      getIndentClass,
      handleReportTypeChange,
      syncTableRowHeights,
      getTooltipIconPosition
    }
  }
})
